import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getQuotationRequests,
  getQuotationRequest,
  updateQuotationRequest,
  assignQuotationRequest,
  requestMoreInfo,
  approveQuotationRequest,
  rejectQuotationRequest,
  convertToQuotation,
  getEmployees,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  MagnifyingGlass,
  Eye,
  UserPlus,
  CheckCircle,
  XCircle,
  ChatText,
  FileText,
  ArrowRight,
  Buildings,
  Clock,
  CalendarBlank,
  CurrencyDollar,
  Envelope,
  Phone,
  User,
  Folder,
  ArrowLeft,
  PaperPlaneTilt,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// Status Badge Component
function StatusBadge({ status }) {
  const { isSpanish } = useLanguage();
  
  const statusConfig = {
    recibida: { color: 'bg-blue-500/20 text-blue-400', label: isSpanish ? 'Recibida' : 'Received' },
    en_revision: { color: 'bg-amber-500/20 text-amber-400', label: isSpanish ? 'En Revisión' : 'Under Review' },
    requiere_mas_info: { color: 'bg-purple-500/20 text-purple-400', label: isSpanish ? 'Requiere Info' : 'Needs Info' },
    aprobada_para_cotizar: { color: 'bg-emerald-500/20 text-emerald-400', label: isSpanish ? 'Aprobada' : 'Approved' },
    rechazada: { color: 'bg-red-500/20 text-red-400', label: isSpanish ? 'Rechazada' : 'Rejected' },
    convertida_a_cotizacion: { color: 'bg-cyan-500/20 text-cyan-400', label: isSpanish ? 'Convertida' : 'Converted' },
  };
  
  const config = statusConfig[status] || { color: 'bg-gray-500/20 text-gray-400', label: status };
  
  return <Badge className={config.color}>{config.label}</Badge>;
}

// Request Detail Component
function RequestDetail({ requestId, onBack, onRefresh }) {
  const { t, isSpanish } = useLanguage();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRequest = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getQuotationRequest(requestId);
      setRequest(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [requestId, t]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await getEmployees({ status: 'activo' });
      setEmployees(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchRequest();
    fetchEmployees();
  }, [fetchEmployees, fetchRequest]);

  const handleAssign = async () => {
    if (!selectedEmployee) return;
    setActionLoading(true);
    try {
      await assignQuotationRequest(requestId, selectedEmployee);
      toast.success(isSpanish ? 'Solicitud asignada' : 'Request assigned');
      setShowAssignDialog(false);
      fetchRequest();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await approveQuotationRequest(requestId);
      toast.success(isSpanish ? 'Solicitud aprobada' : 'Request approved');
      fetchRequest();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await rejectQuotationRequest(requestId, rejectReason);
      toast.success(isSpanish ? 'Solicitud rechazada' : 'Request rejected');
      setShowRejectDialog(false);
      setRejectReason('');
      fetchRequest();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!infoMessage.trim()) return;
    setActionLoading(true);
    try {
      await requestMoreInfo(requestId, infoMessage);
      toast.success(isSpanish ? 'Solicitud de información enviada' : 'Info request sent');
      setShowInfoDialog(false);
      setInfoMessage('');
      fetchRequest();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!confirm(isSpanish ? '¿Convertir a cotización formal?' : 'Convert to formal quotation?')) return;
    setActionLoading(true);
    try {
      const response = await convertToQuotation(requestId);
      toast.success(`${isSpanish ? 'Cotización creada' : 'Quotation created'}: ${response.data.quotation.quotation_number}`);
      fetchRequest();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading || !request) {
    return <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>;
  }

  const canApprove = ['recibida', 'en_revision'].includes(request.status);
  const canReject = !['rechazada', 'convertida_a_cotizacion'].includes(request.status);
  const canConvert = ['aprobada_para_cotizar', 'en_revision', 'recibida'].includes(request.status) && !request.quotation_id;
  const canRequestInfo = !['rechazada', 'convertida_a_cotizacion'].includes(request.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="text-[#9CA3AF] hover:text-[#F3F4F6]">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {isSpanish ? 'Volver' : 'Back'}
        </Button>
        <div className="flex items-center gap-2">
          <StatusBadge status={request.status} />
          <span className="text-[#E2B05E] font-mono font-semibold">{request.request_number}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Request Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="valhalla-card p-6">
            <h2 className="text-xl font-bold text-[#F3F4F6] mb-4">{request.title}</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="flex items-center gap-2 text-[#9CA3AF]">
                <Folder className="w-4 h-4" />
                <span>{request.service_type}</span>
              </div>
              <div className="flex items-center gap-2 text-[#9CA3AF]">
                <Clock className="w-4 h-4" />
                <span>{request.priority}</span>
              </div>
              {request.desired_delivery_date && (
                <div className="flex items-center gap-2 text-[#9CA3AF]">
                  <CalendarBlank className="w-4 h-4" />
                  <span>{request.desired_delivery_date}</span>
                </div>
              )}
              {request.estimated_budget && (
                <div className="flex items-center gap-2 text-[#9CA3AF]">
                  <CurrencyDollar className="w-4 h-4" />
                  <span>${request.estimated_budget.toLocaleString()}</span>
                </div>
              )}
            </div>
            
            <h3 className="text-[#E2B05E] font-semibold mb-2">{isSpanish ? 'Descripción del Requerimiento' : 'Requirement Description'}</h3>
            <p className="text-[#9CA3AF] whitespace-pre-wrap">{request.description}</p>
            
            {request.additional_notes && (
              <>
                <h3 className="text-[#E2B05E] font-semibold mt-4 mb-2">{isSpanish ? 'Notas Adicionales' : 'Additional Notes'}</h3>
                <p className="text-[#9CA3AF]">{request.additional_notes}</p>
              </>
            )}
          </div>

          {/* Messages / Communication */}
          <div className="valhalla-card p-6">
            <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
              <ChatText className="w-5 h-5 text-[#3AB8D6]" />
              {isSpanish ? 'Comunicación' : 'Communication'}
            </h3>
            {request.messages && request.messages.length > 0 ? (
              <div className="space-y-4">
                {request.messages.map((msg, idx) => (
                  <div key={idx} className={`p-4 rounded-lg ${msg.sender === 'equipo' ? 'bg-[#3AB8D6]/10 border-l-4 border-[#3AB8D6]' : 'bg-[#E2B05E]/10 border-l-4 border-[#E2B05E]'}`}>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium text-[#F3F4F6]">{msg.sender_name}</span>
                      <span className="text-xs text-[#6B7280]">{msg.sent_at?.split('T')[0]}</span>
                    </div>
                    <p className="text-[#9CA3AF]">{msg.message}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#9CA3AF] text-center py-4">{isSpanish ? 'Sin mensajes aún' : 'No messages yet'}</p>
            )}
          </div>

          {/* Status History */}
          <div className="valhalla-card p-6">
            <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4">{isSpanish ? 'Historial de Estados' : 'Status History'}</h3>
            <div className="space-y-3">
              {request.status_history?.slice().reverse().map((entry, idx) => (
                <div key={idx} className="flex items-start gap-3 py-2 border-b border-[#242E42] last:border-0">
                  <div className="w-2 h-2 rounded-full mt-2 bg-[#3AB8D6]"></div>
                  <div>
                    <StatusBadge status={entry.status} />
                    <p className="text-sm text-[#9CA3AF] mt-1">{entry.comment}</p>
                    <p className="text-xs text-[#6B7280]">{entry.changed_at?.split('T')[0]}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Info */}
          <div className="valhalla-card p-6">
            <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#E2B05E]" />
              {isSpanish ? 'Contacto' : 'Contact'}
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[#F3F4F6]">
                <User className="w-4 h-4 text-[#9CA3AF]" />
                {request.contact_name}
              </div>
              <div className="flex items-center gap-2 text-[#9CA3AF]">
                <Envelope className="w-4 h-4" />
                {request.contact_email}
              </div>
              {request.contact_phone && (
                <div className="flex items-center gap-2 text-[#9CA3AF]">
                  <Phone className="w-4 h-4" />
                  {request.contact_phone}
                </div>
              )}
              {request.company_name && (
                <div className="flex items-center gap-2 text-[#9CA3AF]">
                  <Buildings className="w-4 h-4" />
                  {request.company_name}
                </div>
              )}
            </div>
          </div>

          {/* Client History */}
          {request.client_history && (
            <div className="valhalla-card p-6">
              <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4">{isSpanish ? 'Historial del Cliente' : 'Client History'}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#9CA3AF]">{isSpanish ? 'Solicitudes previas' : 'Previous requests'}</span>
                  <span className="text-[#F3F4F6]">{request.client_history.total_requests}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#9CA3AF]">{isSpanish ? 'Cotizaciones' : 'Quotations'}</span>
                  <span className="text-[#F3F4F6]">{request.client_history.total_quotations}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#9CA3AF]">{isSpanish ? 'Proyectos' : 'Projects'}</span>
                  <span className="text-[#F3F4F6]">{request.client_history.total_projects}</span>
                </div>
              </div>
            </div>
          )}

          {/* Assignment */}
          <div className="valhalla-card p-6">
            <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4">{isSpanish ? 'Asignación' : 'Assignment'}</h3>
            {request.assigned_employee ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3AB8D6] to-[#2A8FA8] flex items-center justify-center text-white font-semibold">
                  {request.assigned_employee.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <p className="text-[#F3F4F6] font-medium">{request.assigned_employee.name}</p>
                  <p className="text-xs text-[#9CA3AF]">{request.assigned_employee.position}</p>
                </div>
              </div>
            ) : (
              <p className="text-[#9CA3AF]">{isSpanish ? 'No asignada' : 'Not assigned'}</p>
            )}
            <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full mt-4 border-[#242E42]">
                  <UserPlus className="w-4 h-4 mr-2" />
                  {request.assigned_to ? (isSpanish ? 'Reasignar' : 'Reassign') : (isSpanish ? 'Asignar' : 'Assign')}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#151B26] border-[#242E42]">
                <DialogHeader>
                  <DialogTitle className="text-[#F3F4F6]">{isSpanish ? 'Asignar Solicitud' : 'Assign Request'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger className="valhalla-input">
                      <SelectValue placeholder={isSpanish ? 'Seleccionar empleado' : 'Select employee'} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151B26] border-[#242E42]">
                      {employees.map((emp) => (
                        <SelectItem key={emp._id} value={emp._id}>
                          {emp.name} {emp.last_name} - {emp.position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAssign} disabled={!selectedEmployee || actionLoading} className="w-full bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">
                    {actionLoading ? t('loading') : (isSpanish ? 'Asignar' : 'Assign')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Linked Quotation */}
          {request.quotation && (
            <div className="valhalla-card p-6">
              <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#E2B05E]" />
                {isSpanish ? 'Cotización Vinculada' : 'Linked Quotation'}
              </h3>
              <div className="space-y-2">
                <p className="text-[#E2B05E] font-mono">{request.quotation.quotation_number}</p>
                <StatusBadge status={request.quotation.status} />
                <p className="text-[#F3F4F6] font-semibold">${request.quotation.total?.toLocaleString()}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="valhalla-card p-6">
            <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4">{t('actions')}</h3>
            <div className="space-y-3">
              {canApprove && (
                <Button onClick={handleApprove} disabled={actionLoading} className="w-full bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Aprobar para Cotizar' : 'Approve for Quote'}
                </Button>
              )}
              
              {canConvert && (
                <Button onClick={handleConvert} disabled={actionLoading} className="w-full bg-[#3AB8D6] hover:bg-[#2A8FA8]">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Convertir a Cotización' : 'Convert to Quotation'}
                </Button>
              )}
              
              {canRequestInfo && (
                <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full border-[#242E42]">
                      <ChatText className="w-4 h-4 mr-2" />
                      {isSpanish ? 'Solicitar Más Info' : 'Request More Info'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#151B26] border-[#242E42]">
                    <DialogHeader>
                      <DialogTitle className="text-[#F3F4F6]">{isSpanish ? 'Solicitar Información' : 'Request Information'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <Textarea
                        value={infoMessage}
                        onChange={(e) => setInfoMessage(e.target.value)}
                        placeholder={isSpanish ? 'Escriba su mensaje para el cliente...' : 'Write your message to the client...'}
                        className="valhalla-input min-h-32"
                      />
                      <Button onClick={handleRequestInfo} disabled={!infoMessage.trim() || actionLoading} className="w-full bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">
                        <PaperPlaneTilt className="w-4 h-4 mr-2" />
                        {actionLoading ? t('loading') : (isSpanish ? 'Enviar' : 'Send')}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
              
              {canReject && (
                <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10">
                      <XCircle className="w-4 h-4 mr-2" />
                      {isSpanish ? 'Rechazar' : 'Reject'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#151B26] border-[#242E42]">
                    <DialogHeader>
                      <DialogTitle className="text-[#F3F4F6]">{isSpanish ? 'Rechazar Solicitud' : 'Reject Request'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <Textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder={isSpanish ? 'Razón del rechazo...' : 'Reason for rejection...'}
                        className="valhalla-input min-h-24"
                      />
                      <Button onClick={handleReject} disabled={!rejectReason.trim() || actionLoading} className="w-full bg-red-600 hover:bg-red-700">
                        {actionLoading ? t('loading') : (isSpanish ? 'Confirmar Rechazo' : 'Confirm Rejection')}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Page Component
export function QuotationRequestsPage() {
  const { t, isSpanish } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await getQuotationRequests(params);
      setRequests(response.data.requests);
      setStatusCounts(response.data.status_counts);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filteredRequests = requests.filter(req => 
    searchTerm === '' || 
    req.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.request_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (selectedRequest) {
    return (
      <Layout title={isSpanish ? 'Detalle de Solicitud' : 'Request Detail'}>
        <RequestDetail 
          requestId={selectedRequest} 
          onBack={() => setSelectedRequest(null)} 
          onRefresh={fetchRequests}
        />
      </Layout>
    );
  }

  return (
    <Layout title={isSpanish ? 'Solicitudes de Cotización' : 'Quotation Requests'}>
      <div className="space-y-6" data-testid="quotation-requests-page">
        {/* Status Tabs */}
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: isSpanish ? 'Todas' : 'All', count: Object.values(statusCounts).reduce((a, b) => a + b, 0) },
            { value: 'recibida', label: isSpanish ? 'Recibidas' : 'Received', count: statusCounts.recibida || 0 },
            { value: 'en_revision', label: isSpanish ? 'En Revisión' : 'Under Review', count: statusCounts.en_revision || 0 },
            { value: 'requiere_mas_info', label: isSpanish ? 'Requiere Info' : 'Needs Info', count: statusCounts.requiere_mas_info || 0 },
            { value: 'aprobada_para_cotizar', label: isSpanish ? 'Aprobadas' : 'Approved', count: statusCounts.aprobada_para_cotizar || 0 },
            { value: 'convertida_a_cotizacion', label: isSpanish ? 'Convertidas' : 'Converted', count: statusCounts.convertida_a_cotizacion || 0 },
            { value: 'rechazada', label: isSpanish ? 'Rechazadas' : 'Rejected', count: statusCounts.rechazada || 0 },
          ].map((tab) => (
            <Button
              key={tab.value}
              variant={statusFilter === tab.value ? 'default' : 'outline'}
              onClick={() => setStatusFilter(tab.value)}
              className={statusFilter === tab.value ? 'bg-[#E2B05E] text-[#0B0E14]' : 'border-[#242E42]'}
            >
              {tab.label}
              <span className="ml-2 px-2 py-0.5 rounded-full bg-[#0B0E14]/30 text-xs">{tab.count}</span>
            </Button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
          <Input
            type="text"
            placeholder={isSpanish ? 'Buscar por título, número, contacto...' : 'Search by title, number, contact...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 valhalla-input"
          />
        </div>

        {/* Requests Table */}
        {loading ? (
          <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>
        ) : filteredRequests.length === 0 ? (
          <div className="valhalla-card p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#9CA3AF]">{t('noData')}</p>
          </div>
        ) : (
          <div className="valhalla-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#0B0E14]">
                <tr>
                  <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Número' : 'Number'}</th>
                  <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Título' : 'Title'}</th>
                  <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Cliente' : 'Client'}</th>
                  <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Tipo' : 'Type'}</th>
                  <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{t('priority')}</th>
                  <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{t('status')}</th>
                  <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Asignado' : 'Assigned'}</th>
                  <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{t('date')}</th>
                  <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request) => (
                  <tr key={request._id} className="border-t border-[#242E42] hover:bg-[#0B0E14]/50">
                    <td className="py-4 px-6">
                      <span className="text-[#E2B05E] font-mono text-sm">{request.request_number}</span>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-[#F3F4F6] font-medium">{request.title}</p>
                      <p className="text-xs text-[#9CA3AF] truncate max-w-xs">{request.description?.slice(0, 60)}...</p>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-[#F3F4F6]">{request.contact_name}</p>
                      <p className="text-xs text-[#9CA3AF]">{request.company_name || request.client_name}</p>
                    </td>
                    <td className="py-4 px-6 text-[#9CA3AF]">{request.service_type}</td>
                    <td className="py-4 px-6">
                      <Badge variant="outline" className={`border-${request.priority === 'urgente' ? 'red' : request.priority === 'alta' ? 'amber' : 'gray'}-500/50`}>
                        {request.priority}
                      </Badge>
                    </td>
                    <td className="py-4 px-6">
                      <StatusBadge status={request.status} />
                    </td>
                    <td className="py-4 px-6 text-[#9CA3AF]">{request.assigned_to_name || '-'}</td>
                    <td className="py-4 px-6 text-[#9CA3AF] text-sm">{request.created_at?.split('T')[0]}</td>
                    <td className="py-4 px-6 text-center">
                      <Button size="sm" variant="ghost" onClick={() => setSelectedRequest(request._id)} className="text-[#3AB8D6] hover:text-[#F3F4F6]">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default QuotationRequestsPage;
